import type { WeeklyHoursSource } from './Compensation';
import type { PayType } from './employee';
import type { ApiResponse } from './generic';

/**
 * Formation / Training (spec 2026-10-10). Mirrors the backend
 * `modules/trainings` enums and the company-portal API shapes.
 */

export enum TrainingCategory {
  HealthSafety = 'health_safety',
  Compliance = 'compliance',
  Onboarding = 'onboarding',
  Skills = 'skills',
  Other = 'other',
}

export enum TrainingStatus {
  Draft = 'draft',
  Published = 'published',
  Archived = 'archived',
}

export enum TrainingVersionStatus {
  Draft = 'draft',
  Published = 'published',
  Superseded = 'superseded',
}

export enum TrainingChapterType {
  Video = 'video',
  Slides = 'slides',
  Scorm = 'scorm',
}

export enum TrainingVideoSource {
  Upload = 'upload',
  Youtube = 'youtube',
  Vimeo = 'vimeo',
}

export enum TrainingSlidesStatus {
  Processing = 'processing',
  Ready = 'ready',
  Failed = 'failed',
}

/** A SCORM package is unpacked in the background, like a slide deck. */
export enum TrainingScormStatus {
  Processing = 'processing',
  Ready = 'ready',
  Failed = 'failed',
}

/** Why a package could not be used; the builder translates each. */
export type TrainingScormError =
  | 'invalid_package'
  | 'no_manifest'
  | 'multi_sco'
  | 'too_large'
  | 'unsafe_path'
  | 'entry_missing'
  | 'extract_failed';

export type TrainingLanguage = 'fr' | 'en';

/** D117 / D118 limits, checked again by the server. */
export const TRAINING_MAX_VIDEO_BYTES = 1024 ** 3;
export const TRAINING_MAX_SLIDES_BYTES = 50 * 1024 ** 2;
export const TRAINING_MAX_SCORM_BYTES = 250 * 1024 ** 2;
export const TRAINING_VIDEO_CONTENT_TYPES = ['video/mp4', 'video/webm'];
export const TRAINING_PPTX_CONTENT_TYPE =
  'application/vnd.openxmlformats-officedocument.presentationml.presentation';
/** D102: choices per question. */
export const TRAINING_MIN_OPTIONS = 2;
export const TRAINING_MAX_OPTIONS = 8;

export interface TrainingListItem {
  id: string;
  title: string;
  category: TrainingCategory;
  status: TrainingStatus;
  language: TrainingLanguage;
  versionNumber: number;
  hasDraft: boolean;
  chapterCount: number;
  assignedCount: number;
  /** Active or paused rules: 0 with nobody assigned = reaches no one. */
  ruleCount: number;
  completedCount: number;
  overdueCount: number;
  /** null when nobody is assigned yet (shown as N/A). */
  completionPercent: number | null;
  updatedAt: string;
}

export interface TrainingQuestionOption {
  id: string;
  position: number;
  label: string;
  isCorrect: boolean;
}

export interface TrainingQuestion {
  id: string;
  /** null = the final quiz. */
  chapterId: string | null;
  position: number;
  prompt: string;
  options: TrainingQuestionOption[];
}

export interface TrainingChapter {
  id: string;
  position: number;
  title: string;
  introHtml: string | null;
  contentType: TrainingChapterType;
  videoSource: TrainingVideoSource | null;
  videoUrl: string | null;
  videoProviderId: string | null;
  hasVideoUpload: boolean;
  videoDurationSeconds: number | null;
  blockSeeking: boolean;
  slidesStatus: TrainingSlidesStatus | null;
  slidesError: string | null;
  slidesPageCount: number | null;
  minSecondsPerSlide: number;
  scormStatus: TrainingScormStatus | null;
  scormError: TrainingScormError | null;
  scormVersion: '1.2' | '2004' | null;
  scormPackageTitle: string | null;
  scormFileCount: number | null;
  scormSizeBytes: number | null;
  questions: TrainingQuestion[];
}

export interface TrainingVersion {
  id: string;
  versionNumber: number;
  status: TrainingVersionStatus;
  title: string;
  description: string | null;
  language: TrainingLanguage;
  durationMinutes: number | null;
  trainerName: string | null;
  trainerEmail: string | null;
  passMarkPercent: number | null;
  maxAttempts: number | null;
  attestationEnabled: boolean;
  /** Present for Quebec companies only (D122). */
  inTrainingPlan?: boolean;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  chapters: TrainingChapter[];
  finalQuestions: TrainingQuestion[];
}

export interface TrainingDetail {
  id: string;
  category: TrainingCategory;
  /** "Certificate valid for N months" (D194); null = never expires. */
  certificateValidityMonths?: number | null;
  status: TrainingStatus;
  currentVersionId: string | null;
  createdAt: string;
  updatedAt: string;
  assignmentCount: number;
  jurisdiction: { isQuebec: boolean };
  current: TrainingVersion | null;
  draft: TrainingVersion | null;
}

export interface TrainingSettingsPayload {
  title?: string;
  description?: string | null;
  language?: TrainingLanguage;
  durationMinutes?: number | null;
  trainerName?: string | null;
  trainerEmail?: string | null;
  passMarkPercent?: number | null;
  maxAttempts?: number | null;
  attestationEnabled?: boolean;
  inTrainingPlan?: boolean;
}

export interface CreateTrainingPayload extends TrainingSettingsPayload {
  title: string;
  language: TrainingLanguage;
  category?: TrainingCategory;
}

export interface TrainingListFilters {
  status?: TrainingStatus;
  category?: TrainingCategory;
  search?: string;
}

export interface CreateTrainingChapterPayload {
  title: string;
  contentType: TrainingChapterType;
}

export interface UpdateTrainingChapterPayload {
  title?: string;
  introHtml?: string | null;
  contentType?: TrainingChapterType;
  videoSource?: TrainingVideoSource | null;
  videoUrl?: string | null;
  videoS3Key?: string | null;
  videoDurationSeconds?: number | null;
  blockSeeking?: boolean;
  minSecondsPerSlide?: number;
}

export interface TrainingQuestionPayload {
  prompt: string;
  options: { label: string; isCorrect: boolean }[];
}

export interface CreateTrainingQuestionPayload extends TrainingQuestionPayload {
  chapterId?: string | null;
}

export interface TrainingUploadUrl {
  key: string;
  url: string;
}

/** The builder preview of a SCORM chapter: tracks nothing. */
export interface TrainingScormPreview {
  chapterId: string;
  scormVersion: '1.2' | '2004' | null;
  /** Relative to the API base URL. */
  launchPath: string;
}

export interface TrainingChapterMedia {
  chapterId: string;
  videoUrl: string | null;
  slidePages: {
    page: number;
    url: string | null;
    width: number;
    height: number;
  }[];
}

export type TrainingPublishIssueCode =
  | 'no_chapters'
  | 'video_missing'
  | 'video_duration_missing'
  | 'slides_missing'
  | 'slides_processing'
  | 'slides_failed'
  | 'scorm_missing'
  | 'scorm_processing'
  | 'scorm_failed'
  | 'question_invalid';

export interface TrainingPublishIssue {
  code: TrainingPublishIssueCode;
  chapterPosition?: number;
  chapterId?: string;
  questionId?: string;
  questionPosition?: number;
  inFinalQuiz?: boolean;
}

export type GetTrainingsResponse = ApiResponse<TrainingListItem[]>;
export type GetTrainingResponse = ApiResponse<TrainingDetail>;
export type GetTrainingChapterResponse = ApiResponse<TrainingChapter>;
export type GetTrainingQuestionResponse = ApiResponse<TrainingQuestion>;
export type GetTrainingUploadUrlResponse = ApiResponse<TrainingUploadUrl>;
export type GetTrainingChapterMediaResponse = ApiResponse<TrainingChapterMedia>;

// ─── Employee portal: My trainings (PR 2) ───────────────────────────────────

export enum TrainingAssignmentStatus {
  Scheduled = 'scheduled',
  NotStarted = 'not_started',
  InProgress = 'in_progress',
  Completed = 'completed',
  Failed = 'failed',
  Missed = 'missed',
  Excused = 'excused',
  Cancelled = 'cancelled',
}

export interface MyTrainingListItem {
  id: string;
  trainingId: string;
  title: string;
  status: TrainingAssignmentStatus;
  dueAt: string | null;
  overdue: boolean;
  completedAt: string | null;
  scorePercent: number | null;
  durationMinutes: number | null;
  chapterCount: number;
  progressPercent: number;
  /** Completed, on a version that issues a certificate (D114). */
  attestationAvailable: boolean;
}

export interface MyTrainings {
  todo: MyTrainingListItem[];
  completed: MyTrainingListItem[];
}

export interface MyTrainingSummary {
  enabled: boolean;
  open: number;
  overdue: number;
  nextDue: { assignmentId: string; title: string; dueAt: string } | null;
}

export interface MyTrainingQuizQuestion {
  id: string;
  prompt: string;
  /** Several correct choices: checkboxes, "Select all that apply". */
  multiple: boolean;
  options: { id: string; label: string }[];
}

export interface MyTrainingQuiz {
  key: string;
  questions: MyTrainingQuizQuestion[];
  attemptsUsed: number;
  /** null = unlimited. */
  attemptsLeft: number | null;
  bestScorePercent: number | null;
  done: boolean;
}

export interface MyTrainingChapter {
  id: string;
  position: number;
  title: string;
  introHtml: string | null;
  contentType: TrainingChapterType;
  videoSource: TrainingVideoSource | null;
  videoProviderId: string | null;
  videoUrl: string | null;
  videoDurationSeconds: number | null;
  blockSeeking: boolean;
  slidePages: TrainingChapterMedia['slidePages'];
  minSecondsPerSlide: number;
  unlocked: boolean;
  contentDone: boolean;
  done: boolean;
  videoMaxPositionSeconds: number;
  slidesMaxPage: number;
  scormVersion: '1.2' | '2004' | null;
  /** Relative to the API base URL; null while the chapter is locked. */
  scormLaunchPath: string | null;
  scormScorePercent: number | null;
  quiz: MyTrainingQuiz | null;
}

export interface MyTraining {
  id: string;
  status: TrainingAssignmentStatus;
  dueAt: string | null;
  overdue: boolean;
  startedAt: string | null;
  completedAt: string | null;
  scorePercent: number | null;
  versionNumber: number;
  title: string;
  description: string | null;
  language: TrainingLanguage;
  durationMinutes: number | null;
  trainerName: string | null;
  trainerEmail: string | null;
  passMarkPercent: number | null;
  maxAttempts: number | null;
  attestationEnabled: boolean;
  chapters: MyTrainingChapter[];
  finalQuiz: { unlocked: boolean } & Partial<MyTrainingQuiz>;
  hasFinalQuiz: boolean;
}

export interface TrainingHeartbeatPayload {
  position: number;
  playing: boolean;
  rate?: number;
}

export interface TrainingHeartbeatResult {
  maxAllowed: number;
  maxPosition: number;
  chapterComplete: boolean;
  trainingComplete: boolean;
}

export interface TrainingSlideProgressResult {
  maxPage: number;
  nextAllowedAt: string | null;
  chapterComplete: boolean;
  trainingComplete: boolean;
}

export interface TrainingScormProgressResult {
  chapterComplete: boolean;
  trainingComplete: boolean;
  scorePercent: number | null;
}

/** What the sandboxed course frame posts to the player. */
export interface TrainingScormMessage {
  kind: 'kollabhr-scorm';
  chapterId: string;
  reason: 'initialize' | 'change' | 'commit' | 'terminate';
  cmi: Record<string, string>;
}

export interface TrainingQuizAttemptPayload {
  answers: { questionId: string; optionIds: string[] }[];
}

export interface TrainingQuizAttemptResult {
  attemptNumber: number;
  scorePercent: number;
  passed: boolean | null;
  attemptsLeft: number | null;
  assignmentStatus: TrainingAssignmentStatus;
  trainingComplete: boolean;
  questions: { id: string; correct: boolean; correctOptionIds?: string[] }[];
}

// ─── Assignment rules (PR 3) ────────────────────────────────────────────────

/** D105: the four schedule types, the same vocabulary as Surveys. */
export enum TrainingScheduleType {
  Now = 'now',
  OnDate = 'on_date',
  AfterJoining = 'after_joining',
  Recurring = 'recurring',
}

/** Who a rule covers; `branch` is shown as "Location". */
export enum TrainingTargetType {
  All = 'all',
  Department = 'department',
  Branch = 'branch',
  Employee = 'employee',
}

export enum TrainingRuleStatus {
  Active = 'active',
  Paused = 'paused',
  Ended = 'ended',
}

export interface TrainingRuleTarget {
  targetType: TrainingTargetType;
  /** null for `all`. */
  targetId: string | null;
}

export interface TrainingRule {
  id: string;
  scheduleType: TrainingScheduleType;
  /** YYYY-MM-DD (on a date, recurring). */
  startsOn: string | null;
  daysAfterJoining: number | null;
  /** After joining in calendar months, instead of days. */
  monthsAfterJoining?: number | null;
  intervalYears: number | null;
  completionWindowDays: number | null;
  status: TrainingRuleStatus;
  cycleNumber: number;
  nextCycleAt: string | null;
  createdAt: string;
  targets: TrainingRuleTarget[];
  /** People the audience covers today. */
  coveredCount: number;
  /** Assignments this rule made (cancelled ones excluded). */
  assignedCount: number;
  /** When the rule next acts on its own; null = nothing planned. */
  nextRunAt: string | null;
}

export interface TrainingRulePayload {
  targets: { targetType: TrainingTargetType; targetId?: string | null }[];
  scheduleType: TrainingScheduleType;
  startsOn?: string;
  daysAfterJoining?: number;
  /** After joining in calendar months, instead of days. */
  monthsAfterJoining?: number;
  intervalYears?: number;
  completionWindowDays?: number;
  /**
   * Now, On a date or Recurring: also a rule for new hires X days (or
   * months) after joining. Beside a one-time assignment it only takes
   * people who join after it is saved.
   */
  newHiresDaysAfterJoining?: number;
  newHiresMonthsAfterJoining?: number;
}

/** A recurring assignment repeats every 1 to 10 years. */
export const TRAINING_INTERVAL_YEARS = [1, 2, 3, 4, 5, 10] as const;

/** How "after joining" is counted. */
export type TrainingAfterJoiningUnit = 'days' | 'months';

export interface UpdateTrainingRulePayload {
  targets?: TrainingRulePayload['targets'];
  completionWindowDays?: number | null;
}

export interface TrainingRulePreview {
  count: number;
  sample: { id: string; name: string }[];
  /** Who, among them, has no email and will not be notified (D163). */
  unreachable?: { count: number; employees: { id: string; name: string }[] };
}

export interface CreateTrainingRuleResult {
  ruleIds: string[];
  assigned: number;
}

export type GetTrainingRulesResponse = ApiResponse<TrainingRule[]>;
export type TrainingRulePreviewResponse = ApiResponse<TrainingRulePreview>;
export type CreateTrainingRuleResponse = ApiResponse<CreateTrainingRuleResult>;

export type GetMyTrainingsResponse = ApiResponse<MyTrainings>;
export type GetMyTrainingSummaryResponse = ApiResponse<MyTrainingSummary>;
export type GetMyTrainingResponse = ApiResponse<MyTraining>;
export type TrainingHeartbeatResponse = ApiResponse<TrainingHeartbeatResult>;
export type TrainingSlideProgressResponse =
  ApiResponse<TrainingSlideProgressResult>;
export type TrainingScormProgressResponse =
  ApiResponse<TrainingScormProgressResult>;
export type GetTrainingScormPreviewResponse = ApiResponse<TrainingScormPreview>;
export type TrainingQuizAttemptResponse =
  ApiResponse<TrainingQuizAttemptResult>;

// ─── Results, HR actions, history, analytics (PR 4) ─────────────────────────

/** Stored statuses plus the computed ones a person reads. */
export type TrainingDisplayStatus =
  | `${TrainingAssignmentStatus}`
  | 'overdue'
  | 'completed_late';

export interface TrainingResultRow {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeNumber: string | null;
  department: string | null;
  location: string | null;
  status: TrainingAssignmentStatus;
  displayStatus: TrainingDisplayStatus;
  cycleNumber: number;
  deliveredAt: string | null;
  dueAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  scorePercent: number | null;
  attemptsUsed: number;
  timeSpentSeconds: number;
  versionNumber: number;
  recordedByHr: boolean;
  hrNote: string | null;
  excuseReason: string | null;
  /** When "Send reminder" is possible again; null = now. */
  nextReminderAt: string | null;
  attestationAvailable: boolean;
}

export type TrainingResultStatusFilter =
  | 'scheduled'
  | 'not_started'
  | 'in_progress'
  | 'overdue'
  | 'completed'
  | 'failed'
  | 'missed'
  | 'excused'
  | 'cancelled';

export interface TrainingResultsFilters {
  cycle?: number;
  status?: TrainingResultStatusFilter;
  departmentId?: string;
  branchId?: string;
  search?: string;
}

export interface TrainingResultsQuery extends TrainingResultsFilters {
  page?: number;
  limit?: number;
}

export interface TrainingResultsPage {
  items: TrainingResultRow[];
  cycles: number[];
  meta: {
    page: number;
    limit: number;
    totalCount: number;
    lastPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export interface TrainingResultsSummary {
  assigned: number;
  completed: number;
  completionPercent: number | null;
  excused: number;
  failed: number;
  overdue: number;
  open: number;
  averageScore: number | null;
  averageTimeSeconds: number | null;
}

export interface TrainingAttemptReview {
  quizKey: string;
  chapterTitle: string | null;
  attemptNumber: number;
  scorePercent: number;
  passed: boolean | null;
  submittedAt: string;
  questions: {
    id: string;
    prompt: string;
    correct: boolean;
    options: {
      id: string;
      label: string;
      isCorrect: boolean;
      chosen: boolean;
    }[];
  }[];
}

export interface TrainingResultDetail extends TrainingResultRow {
  title: string;
  chapters: {
    id: string;
    position: number;
    title: string;
    done: boolean;
    completedAt: string | null;
    contentType: TrainingChapterType;
    /** Time counted on this chapter (older time is only in the total). */
    timeSpentSeconds: number;
    /** Slides chapters: seconds on each page, index 0 = page 1. */
    slideSeconds: number[] | null;
  }[];
  attempts: TrainingAttemptReview[];
}

export interface TrainingHistoryRow {
  id: string;
  trainingId: string;
  employeeId: string;
  employeeName: string;
  title: string;
  category: TrainingCategory;
  status: TrainingAssignmentStatus;
  displayStatus: TrainingDisplayStatus;
  cycleNumber: number;
  deliveredAt: string | null;
  dueAt: string | null;
  completedAt: string | null;
  scorePercent: number | null;
  attemptsUsed: number;
  versionNumber: number;
  recordedByHr: boolean;
  attestationAvailable: boolean;
}

export interface TrainingAnalytics {
  kpis: {
    completionPercent: number | null;
    overdue: number;
    averageScore: number | null;
    hoursYearToDate: number;
    assigned: number;
    completed: number;
  };
  trainings: {
    id: string;
    title: string;
    category: TrainingCategory;
    assigned: number;
    completed: number;
    completionPercent: number | null;
    overdue: number;
    averageScore: number | null;
  }[];
}

export type GetTrainingResultsResponse = ApiResponse<TrainingResultsPage>;
export type GetTrainingResultsSummaryResponse =
  ApiResponse<TrainingResultsSummary>;
export type GetTrainingResultDetailResponse = ApiResponse<TrainingResultDetail>;
export type GetTrainingHistoryResponse = ApiResponse<TrainingHistoryRow[]>;
export type GetTrainingAnalyticsResponse = ApiResponse<TrainingAnalytics>;

// ─── External trainings and spend (PR 4.5, D123 to D129) ───────────────────

export const EXTERNAL_PROVIDER_TYPES = [
  'institution',
  'accredited',
  'not_accredited',
] as const;
export type ExternalProviderType = (typeof EXTERNAL_PROVIDER_TYPES)[number];

export const EXTERNAL_TRAINING_FORMATS = [
  'course',
  'conference',
  'mentoring_coaching',
  'on_the_job',
  'apprenticeship',
] as const;
export type ExternalTrainingFormat = (typeof EXTERNAL_TRAINING_FORMATS)[number];

export type ExternalTrainingStatus = 'pending' | 'approved' | 'rejected';

export const EXTERNAL_COST_KEYS = [
  'tuition',
  'travel',
  'meals',
  'lodging',
  'materials',
  'other',
] as const;
export type ExternalCostKey = (typeof EXTERNAL_COST_KEYS)[number];

export const TRAINING_SPEND_CATEGORIES = [
  'trainee_wages',
  'tuition_fees',
  'internal_trainer',
  'travel',
  'meals',
  'lodging',
  'materials',
  'committee',
  'content_development',
  'equipment_premises',
  'other',
] as const;
export type TrainingSpendCategory = (typeof TRAINING_SPEND_CATEGORIES)[number];

/** Expense categories HR picks (wages come from the ledger itself). */
export const TRAINING_EXPENSE_CATEGORIES = TRAINING_SPEND_CATEGORIES.filter(
  (c) => c !== 'trainee_wages'
);

export const SPEND_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
] as const;
export type SpendFileType = (typeof SPEND_FILE_TYPES)[number];
export const SPEND_FILE_MAX_COUNT = 5;

export type EligibilityHint = 'likely' | 'check';

export interface SpendFile {
  id: string;
  kind: 'receipt' | 'attestation';
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

export interface ExternalTraining {
  id: string;
  employeeId: string;
  employeeName: string;
  title: string;
  providerName: string;
  trainerName: string | null;
  providerType: ExternalProviderType | null;
  accreditationNumber: string | null;
  format: ExternalTrainingFormat;
  startsOn: string;
  endsOn: string;
  hours: number;
  paidDuringTraining: boolean;
  costs: Record<ExternalCostKey, number> & { total: number };
  currency: string;
  status: ExternalTrainingStatus;
  rejectionReason: string | null;
  /** Certificate "valid until" (D194), or null. */
  validUntil?: string | null;
  notes: string | null;
  addedByHr: boolean;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
  files: SpendFile[];
  /** HR in a Quebec company only. */
  eligibilityHint?: EligibilityHint;
  countsToward1pct?: boolean;
  /** HR with `employee_salary.read` only. */
  wageCostOverride?: number | null;
}

/** An approved entry as a manager sees it (no costs, X6). */
export interface ExternalTrainingBrief {
  id: string;
  title: string;
  providerName: string;
  startsOn: string;
  endsOn: string;
  hours: number;
  costs?: ExternalTraining['costs'];
  currency?: string;
}

export interface ExternalTrainingPayload {
  title: string;
  providerName: string;
  trainerName?: string | null;
  providerType?: ExternalProviderType | null;
  accreditationNumber?: string | null;
  format: ExternalTrainingFormat;
  startsOn: string;
  endsOn: string;
  hours: number;
  paidDuringTraining: boolean;
  costTuition?: number;
  costTravel?: number;
  costMeals?: number;
  costLodging?: number;
  costMaterials?: number;
  costOther?: number;
  /** Certificate "valid until" (D194), optional. */
  validUntil?: string | null;
  notes?: string | null;
}

export interface HrExternalTrainingPayload extends ExternalTrainingPayload {
  employeeId: string;
}

export interface ExternalTrainingFilters {
  status?: ExternalTrainingStatus;
  employeeId?: string;
  year?: number;
  search?: string;
}

export interface MyExternalTrainings {
  quebec: boolean;
  currency: string;
  items: ExternalTraining[];
}

export interface SpendUploadTicket {
  key: string;
  url: string;
}

export type TrainingSpendLineSource = 'internal' | 'external' | 'expense';

/** One salary row and the weekly hours a line's hourly wage is worked out on. */
export interface TrainingWageBasis {
  salaryId: string;
  amount: number;
  payType: PayType | null;
  weeklyHours: number;
  weeklyHoursSource: WeeklyHoursSource;
  /** The schedule's weekly hours, used when the salary has none. */
  scheduleWeeklyHours: number;
  scheduleSource: WeeklyHoursSource;
}

export interface TrainingSpendLine {
  key: string;
  source: TrainingSpendLineSource;
  id: string;
  date: string;
  employeeId: string | null;
  employeeName: string | null;
  title: string;
  trainingId: string | null;
  hours: number | null;
  amounts: Partial<Record<TrainingSpendCategory, number | null>>;
  total: number;
  wage: {
    hidden: boolean;
    noSalary: boolean;
    edited: boolean;
    hourly: number | null;
    /** The salary the hourly wage comes from (salary readers only). */
    basis?: TrainingWageBasis | null;
  } | null;
  hasReceipt: boolean | null;
  eligibilityHint?: EligibilityHint;
  countsToward1pct?: boolean;
}

export type TrainingHoursBasis = 'set_duration' | 'tracked_time';

export interface TrainingSpendSettings {
  hoursBasis: TrainingHoursBasis;
  employerContributionsPercent: number;
  quebec: boolean;
  currency: string;
}

export interface TrainingSpendLedger {
  year: number;
  currency: string;
  quebec: boolean;
  salaryVisible: boolean;
  settings: Omit<TrainingSpendSettings, 'quebec' | 'currency'>;
  lines: TrainingSpendLine[];
  totals: Partial<Record<TrainingSpendCategory, number>>;
  total: number;
  hours: number;
}

export interface TrainingExpense {
  id: string;
  category: TrainingSpendCategory;
  spentOn: string;
  amount: number;
  currency: string;
  description: string;
  trainingId: string | null;
  eligibilityHint?: EligibilityHint;
  countsToward1pct?: boolean;
  files?: SpendFile[];
}

export interface TrainingExpensePayload {
  category: TrainingSpendCategory;
  spentOn: string;
  amount: number;
  description: string;
  trainingId?: string | null;
  countsToward1pct?: boolean;
}

export interface TrainingSpendLinePayload {
  countsToward1pct?: boolean;
  wageCostOverride?: number | null;
}

export type GetExternalTrainingsResponse = ApiResponse<ExternalTraining[]>;
export type ExternalTrainingResponse = ApiResponse<ExternalTraining>;
export type GetMyExternalTrainingsResponse = ApiResponse<MyExternalTrainings>;
export type GetExternalTrainingBriefsResponse = ApiResponse<
  ExternalTrainingBrief[]
>;
export type SpendUploadTicketResponse = ApiResponse<SpendUploadTicket>;
export type SpendFileResponse = ApiResponse<SpendFile>;
export type SpendSignedUrlResponse = ApiResponse<{
  url: string;
  fileName: string;
}>;
export type GetTrainingSpendLedgerResponse = ApiResponse<TrainingSpendLedger>;
export type TrainingSpendSettingsResponse = ApiResponse<TrainingSpendSettings>;
export type TrainingExpenseResponse = ApiResponse<TrainingExpense>;

// ─── Quebec 1% law report (PR 4.6, D128) ───────────────────────────────────

export const SPEND_YEAR_FILE_KINDS = ['plan', 'minutes', 'other'] as const;
export type SpendYearFileKind = (typeof SPEND_YEAR_FILE_KINDS)[number];

export interface SommaireLines {
  applies: boolean;
  exempt: boolean;
  line71: number | null;
  line73: number | null;
  line74: number | null;
  line75: number | null;
  line79_1: number;
  line79_2: number;
  line79_3: number;
  line79_4: number | null;
  line79_5: number | null;
}

export interface QuebecReport {
  year: number;
  currency: 'CAD';
  threshold: number;
  payroll: {
    estimate: number;
    override: number | null;
    value: number;
    source: 'estimate' | 'hr';
    employees: number;
  };
  eligible: number;
  aid: number;
  carriedIn: { value: number; source: 'hr' | 'previous_year' | 'none' };
  cqif: boolean;
  notes: string | null;
  lines: SommaireLines;
  categories: {
    category: TrainingSpendCategory;
    total: number;
    count: number;
  }[];
  counted: TrainingSpendLine[];
  dueDate: string;
  files: (Omit<SpendFile, 'kind'> & { kind: SpendYearFileKind })[];
}

export interface SpendYearPayload {
  payrollOverride?: number | null;
  governmentAid?: number | null;
  carriedIn?: number | null;
  cqifExempt?: boolean;
  notes?: string | null;
}

export type QuebecReportResponse = ApiResponse<QuebecReport>;
